@if (action) {
    <div class="tw-py-3 tw-flex tw-flex-col tw-items-start">
        <app-action-btn variant="stroked" [disabled]="disabled" (click)="actionRun.emit(action.actionId)">
            @if (action.icon) {
                <mat-icon>{{ action.icon }}</mat-icon>
            }
            <span>{{ action.label | translate: action.labelParams }}</span>
        </app-action-btn>
        @if (action.description) {
            <div class="tw-text-xs tw-text-[var(--sys-secondary-text)] tw-mt-1">{{ action.description | translate }}</div>
        }
    </div>
} @else if (device) {
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3">
        <div class="tw-min-w-0">
            <div class="tw-font-medium tw-text-[var(--sys-text)]">{{ device.label | translate: device.labelParams }}</div>
            @if (device.description) {
                <div class="tw-text-xs tw-text-[var(--sys-secondary-text)]">{{ device.description | translate }}</div>
            }
        </div>
        <mat-slide-toggle
            [checked]="value === true"
            [disabled]="disabled"
            (change)="onDeviceToggle($event)" />
    </div>
} @else if (componentType) {
    <ng-container *ngComponentOutlet="componentType" />
} @else if (preference) {
    @if (preference.control === 'object-list' && preference.objectList) {
        <div class="tw-py-2">
            <div class="tw-text-[var(--sys-text)] tw-font-medium">{{ preference.label | translate }}</div>
            <app-settings-object-list
                [config]="preference.objectList"
                [context]="context"
                [items]="recordsValue"
                [lookup]="lookup"
                [disabled]="disabled"
                (itemsChange)="valueChange.emit($event)"
                (addRequest)="actionRun.emit(preference.objectList.addAction!)" />
            @if (error && !hasStartedRows) {
                <div class="tw-text-xs tw-text-red-600">{{ errorLabel | translate }}</div>
            }
        </div>
    } @else if (preference.control === 'indexed-texts' && preference.indexedTexts) {
        <div class="tw-py-2 tw-flex tw-flex-col tw-gap-4">
            @for (index of textIndexes; track index) {
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-w-full">
                    <mat-label>{{ preference.indexedTexts.itemLabel | translate: { value: index } }}</mat-label>
                    <input matInput type="text"
                        [ngModel]="textAt(index)"
                        [disabled]="disabled"
                        (ngModelChange)="setIndexedText(index, $event)" />
                </mat-form-field>
            }
        </div>
    } @else if (preference.control === 'image') {
        <div class="tw-py-2 tw-flex tw-flex-col tw-gap-2">
            <div class="tw-text-[var(--sys-text)] tw-font-medium">{{ preference.label | translate }}</div>
            <div class="tw-flex tw-items-center tw-gap-3">
                @if (value) {
                    <img [src]="value" class="tw-h-24 tw-rounded-lg" alt="" />
                    <button mat-icon-button type="button" [disabled]="disabled" (click)="valueChange.emit('')"><mat-icon>delete</mat-icon></button>
                } @else {
                    <app-action-btn variant="stroked" [disabled]="disabled" (click)="imageInput.click()">
                        <mat-icon>add_circle</mat-icon>
                        {{ 'SETTINGS.SETTINGS.FIDELITY_ADD_IMAGE' | translate }}
                    </app-action-btn>
                }
            </div>
            <input #imageInput type="file" accept="image/*" class="tw-hidden" (change)="onImageSelected($event)" />
        </div>
    } @else if (preference.control === 'toggle') {
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3">
            <div class="tw-min-w-0">
                <div class="tw-font-medium tw-text-[var(--sys-text)]">{{ preference.label | translate: preference.labelParams }}</div>
                @if (preference.description) {
                    <div class="tw-text-xs tw-text-[var(--sys-secondary-text)]">{{ preference.description | translate }}</div>
                }
            </div>
            <mat-slide-toggle
                [checked]="value === true"
                [disabled]="disabled"
                (change)="valueChange.emit($event.checked)" />
        </div>
    } @else {
        <div class="tw-py-2">
            @if (preference.description) {
                <div class="tw-text-xs tw-text-[var(--sys-secondary-text)] tw-mb-2">{{ preference.description | translate }}</div>
            }
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-w-full">
                <mat-label>{{ preference.label | translate: preference.labelParams }}</mat-label>
                @switch (preference.control) {
                    @case ('select') {
                        <mat-select panelClass="settings-select-panel"
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)">
                            @for (option of options; track option.value) {
                                <mat-option [value]="option.value">{{ option.translate ? (option.label | translate: option.labelParams) : option.label }}</mat-option>
                            }
                        </mat-select>
                    }
                    @case ('multiselect') {
                        <mat-select panelClass="settings-select-panel" multiple
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)">
                            @for (option of options; track option.value) {
                                <mat-option [value]="option.value">{{ option.translate ? (option.label | translate: option.labelParams) : option.label }}</mat-option>
                            }
                        </mat-select>
                    }
                    @case ('chips') {
                        <mat-chip-grid #chipGrid [disabled]="disabled">
                            @for (item of listValue; track item) {
                                <mat-chip-row (removed)="removeChip(item)">
                                    {{ item }}
                                    <button matChipRemove type="button"><mat-icon>cancel</mat-icon></button>
                                </mat-chip-row>
                            }
                        </mat-chip-grid>
                        <input
                            [matChipInputFor]="chipGrid"
                            [matChipInputSeparatorKeyCodes]="chipSeparatorKeys"
                            [matChipInputAddOnBlur]="true"
                            [disabled]="disabled || !canAddChip"
                            (matChipInputTokenEnd)="addChip($event)" />
                    }
                    @case ('number') {
                        <input matInput type="number"
                            [min]="preference.min ?? null"
                            [max]="preference.max ?? null"
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)" />
                    }
                    @case ('item-picker') {
                        <input matInput type="text" readonly class="tw-cursor-pointer"
                            [value]="itemName"
                            [disabled]="disabled"
                            (click)="pickItem()" />
                    }
                    @case ('color') {
                        <input matInput type="color"
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)" />
                    }
                    @case ('textarea') {
                        <textarea matInput rows="3"
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)"></textarea>
                    }
                    @default {
                        <input matInput [type]="preference.control === 'email' ? 'email' : 'text'"
                            [ngModel]="value"
                            [disabled]="disabled"
                            (ngModelChange)="valueChange.emit($event)" />
                    }
                }
                @if (preference.suffix) {
                    <span matTextSuffix>{{ preference.suffix }}</span>
                }
                @if (preference.lookup; as lookupId) {
                    <button matSuffix mat-icon-button type="button" [disabled]="disabled" (click)="pickNumber(lookupId)">
                        <mat-icon>fact_check</mat-icon>
                    </button>
                }
                @if (error) {
                    <mat-hint class="tw-text-red-600">{{ errorLabel | translate }}</mat-hint>
                }
            </mat-form-field>
        </div>
    }
}
